/**
 * Utilidades para formatear fechas en formato DD/MM/YYYY
 */

/**
 * Formatea una fecha en formato DD/MM/YYYY
 * @param date - Fecha en formato string (YYYY-MM-DD) o Date
 * @returns Fecha formateada como DD/MM/YYYY
 */
export function formatDateDDMMYYYY(date: string | Date): string {
  let fecha: Date;
  
  if (typeof date === 'string') {
    // Si es string, intentar parsear
    fecha = new Date(date);
    if (isNaN(fecha.getTime())) {
      // Si falla, intentar parsear formato YYYY-MM-DD
      const parts = date.split('-');
      if (parts.length === 3) {
        fecha = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        return date; // Retornar original si no se puede parsear
      }
    }
  } else {
    fecha = date;
  }

  if (isNaN(fecha.getTime())) {
    return date.toString(); // Retornar original si no es válida
  }

  const dia = fecha.getDate().toString().padStart(2, '0');
  const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
  const año = fecha.getFullYear();

  return `${dia}/${mes}/${año}`;
}

/**
 * Formatea una fecha con hora en formato DD/MM/YYYY HH:MM
 * @param date - Fecha en formato string o Date
 * @returns Fecha y hora formateada como DD/MM/YYYY HH:MM
 */
export function formatDateTimeDDMMYYYY(date: string | Date): string {
  let fecha: Date;
  
  if (typeof date === 'string') {
    fecha = new Date(date);
    if (isNaN(fecha.getTime())) {
      return date; // Retornar original si no se puede parsear
    }
  } else {
    fecha = date;
  }

  if (isNaN(fecha.getTime())) {
    return date.toString(); // Retornar original si no es válida
  }

  const dia = fecha.getDate().toString().padStart(2, '0');
  const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
  const año = fecha.getFullYear();
  const horas = fecha.getHours().toString().padStart(2, '0');
  const minutos = fecha.getMinutes().toString().padStart(2, '0');

  return `${dia}/${mes}/${año} ${horas}:${minutos}`;
}

/**
 * Formatea una fecha con hora completa en formato DD/MM/YYYY HH:MM:SS
 * @param date - Fecha en formato string o Date
 * @returns Fecha y hora completa formateada como DD/MM/YYYY HH:MM:SS
 */
export function formatDateTimeFullDDMMYYYY(date: string | Date): string {
  let fecha: Date;
  
  if (typeof date === 'string') {
    fecha = new Date(date);
    if (isNaN(fecha.getTime())) {
      return date; // Retornar original si no se puede parsear
    }
  } else {
    fecha = date;
  }

  if (isNaN(fecha.getTime())) {
    return date.toString(); // Retornar original si no es válida
  }

  const dia = fecha.getDate().toString().padStart(2, '0');
  const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
  const año = fecha.getFullYear();
  const horas = fecha.getHours().toString().padStart(2, '0');
  const minutos = fecha.getMinutes().toString().padStart(2, '0');
  const segundos = fecha.getSeconds().toString().padStart(2, '0');

  return `${dia}/${mes}/${año} ${horas}:${minutos}:${segundos}`;
}

/**
 * Convierte una fecha de formato DD-MM-YYYY a formato YYYY-MM-DD (ISO)
 * @param fechaDDMMYYYY - Fecha en formato DD-MM-YYYY
 * @returns Fecha en formato YYYY-MM-DD o string vacío si no es válida
 */
export function convertirDDMMYYYYaISO(fechaDDMMYYYY: string): string {
  if (!fechaDDMMYYYY) return '';
  
  // Remover espacios y guiones
  const fechaLimpia = fechaDDMMYYYY.trim().replace(/\s+/g, '');
  
  // Intentar parsear formato DD-MM-YYYY
  const partes = fechaLimpia.split('-');
  if (partes.length === 3) {
    const dia = partes[0].padStart(2, '0');
    const mes = partes[1].padStart(2, '0');
    const año = partes[2];
    
    // Validar que sean números
    if (/^\d+$/.test(dia) && /^\d+$/.test(mes) && /^\d+$/.test(año)) {
      const diaNum = parseInt(dia, 10);
      const mesNum = parseInt(mes, 10);
      const añoNum = parseInt(año, 10);
      
      // Validar rangos
      if (diaNum >= 1 && diaNum <= 31 && mesNum >= 1 && mesNum <= 12 && añoNum >= 1900 && añoNum <= 2100) {
        // Crear fecha para validar que sea válida (ej: no 31 de febrero)
        const fecha = new Date(añoNum, mesNum - 1, diaNum);
        if (fecha.getDate() === diaNum && fecha.getMonth() === mesNum - 1 && fecha.getFullYear() === añoNum) {
          return `${año}-${mes}-${dia}`;
        }
      }
    }
  }
  
  return '';
}

/**
 * Convierte una fecha de formato YYYY-MM-DD (ISO) a formato DD-MM-YYYY
 * @param fechaISO - Fecha en formato YYYY-MM-DD
 * @returns Fecha en formato DD-MM-YYYY o string vacío si no es válida
 */
export function convertirISOaDDMMYYYY(fechaISO: string): string {
  if (!fechaISO) return '';
  
  // Intentar parsear formato YYYY-MM-DD
  const partes = fechaISO.split('-');
  if (partes.length === 3) {
    const año = partes[0];
    const mes = partes[1];
    const dia = partes[2];
    
    // Validar que sean números
    if (/^\d+$/.test(año) && /^\d+$/.test(mes) && /^\d+$/.test(dia)) {
      return `${dia}-${mes}-${año}`;
    }
  }
  
  // Si no es formato ISO, intentar parsear como Date
  const fecha = new Date(fechaISO);
  if (!isNaN(fecha.getTime())) {
    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const año = fecha.getFullYear();
    return `${dia}-${mes}-${año}`;
  }
  
  return '';
}